import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { DropdownButton, MenuItem as RBMenuItem } from 'ui';
import CheckIcon from '@mui/icons-material/Check';
import UserAvatarBase from 'projects/components/UserAvatarBase';
import { calculateInitials } from 'utils/userUtils';
import useUserColor from 'hooks/users/useUserColor';
import { BOT_USER_ID } from 'constants/UserConstants';
import { displayName } from './dashboardUtils';
import OverflowTooltip from './OverflowTooltip';
import ColorConstants from 'constants/ColorConstants';

const ROW_HEIGHT = 22;

function AssigneeRow({ assignee }) {
  const color = useUserColor(assignee.user_id);
  const initials = calculateInitials({ ...assignee, id: assignee.user_id });
  const isBot = assignee.user_id === BOT_USER_ID;
  const name = displayName(assignee);

  const row = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: ROW_HEIGHT, minWidth: 0 }}>
      <UserAvatarBase size={22} borderRadius="50%" fontSize="11px" color={color} initials={initials} />
      {isBot ? (
        // The bot row carries its own explanatory tooltip below; its name is short.
        (<Typography sx={{ fontSize: 13, color: ColorConstants.GRAY_900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name}
        </Typography>)
      ) : (
        // Long names/emails are truncated; reveal the full value on hover.
        (<OverflowTooltip sx={{ fontSize: 13, color: ColorConstants.GRAY_900 }}>{name}</OverflowTooltip>)
      )}
    </Box>
  );

  if (isBot) {
    return (
      <Tooltip title="Assigned automatically because the AI coded this transcript" placement="top-start">
        {row}
      </Tooltip>
    );
  }
  return row;
}

function AssigneeCell({
  row,
  people,
  visibleAssignees,
  hiddenCount,
  isExpanded,
  onToggleExpanded,
  canAssign,
  onToggleAssign,
}) {
  const assignees = row.assignees || [];

  const assigneeRows = visibleAssignees.map(assignee => (
    <AssigneeRow key={assignee.user_id} assignee={assignee} />
  ));

  // The +N more / See less expander sits BELOW the assign trigger (not inside
  // it) so expanding the list never collides with opening the assign menu.
  const moreToggle = hiddenCount > 0 && (
    <Box
      component="button"
      type="button"
      aria-expanded={isExpanded}
      onClick={onToggleExpanded}
      sx={{
        fontSize: 12, color: ColorConstants.ACTIVE_BUTTON_BLUE, cursor: 'pointer', mt: 0.25, userSelect: 'none',
        // strip native button chrome so it still reads as inline text; keep it
        // content-width (not stretched by the flex column) and left-aligned
        appearance: 'none', border: 'none', background: 'none', p: 0,
        fontFamily: 'inherit', textAlign: 'left', alignSelf: 'flex-start',
      }}
    >
      {isExpanded ? 'See less' : `+${hiddenCount} more`}
    </Box>
  );

  // View-only: just the names (or "Unassigned"), no menu.
  if (!canAssign) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {assignees.length > 0
          ? assigneeRows
          : <Typography sx={{ fontSize: 13, color: ColorConstants.GRAY_400 }}>Unassigned</Typography>}
        {moreToggle}
      </Box>
    );
  }

  // Editors: the assignee area is the assign trigger — clicking anywhere on it
  // opens the menu. "Assign to…" shows only when the cell is empty; a populated
  // cell shows its assignees and still opens the menu on click.
  const staleAssignees = assignees
    .filter(a => a.user_id !== BOT_USER_ID && !people.some(p => String(p.id) === String(a.user_id)))
    .map(a => ({ id: a.user_id, name: a.name, email: a.email }));
  const options = [...people, ...staleAssignees];

  const title = assignees.length > 0
    ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{assigneeRows}</Box>
    : <Typography sx={{ fontSize: 13, color: ColorConstants.ACTIVE_BUTTON_BLUE }}>Assign to…</Typography>; // empty-cell assign CTA — accent blue so it reads as a clickable link

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <DropdownButton
        variant="link"
        noCaret
        title={title}
        id={`assign-transcript-${row.id}`}
        style={{ padding: 0, width: '100%', textAlign: 'left', whiteSpace: 'normal', textDecoration: 'none' }}
      >
        {options.map(person => {
          const assigned = assignees.some(a => String(a.user_id) === String(person.id));
          // Balance the menu padding: 12px + the 22px check gutter = 34px on
          // both sides of the names, with an even 6px vertical rhythm. The
          // popup renders in a portal, so style the items directly.
          return (
            <RBMenuItem key={person.id} onClick={() => onToggleAssign(person, assigned)} style={{ padding: '6px 34px 6px 12px' }}>
              <span style={{ display: 'inline-flex', width: 22, justifyContent: 'flex-start', verticalAlign: 'middle' }}>
                {assigned && <CheckIcon style={{ fontSize: 16, color: 'inherit' }} />}
              </span>
              {displayName(person)}
            </RBMenuItem>
          );
        })}
      </DropdownButton>
      {moreToggle}
    </Box>
  );
}

export default AssigneeCell;
